<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :isDarkModeProp="isDarkMode"
      @navigate="handleNavigation"
    />

    <v-main class="dealer-main" :class="{ 'dark-mode': isDarkMode }">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <h1 class="text-h4 font-weight-bold text-white mb-0" style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); padding: 20px; border-radius: 8px;">
            <span class="material-icons-round mr-3" style="vertical-align: middle;">account_balance_wallet</span>
            Dealer Portal - Commission
          </h1>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          <p class="mt-4">Loading commission data...</p>
        </div>

        <template v-else>
          <!-- Referring Agent Section (if referred by an agent) -->
          <v-card v-if="referringAgent" class="mb-6 agent-card" elevation="3">
            <v-card-title class="text-h5 font-weight-bold success--text">
              <span class="material-icons-round mr-2">handshake</span>
              Your Referring Agent
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row align="center">
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <v-avatar size="64" class="mr-4" color="success">
                      <img v-if="referringAgent.avatar" :src="wasabiUrl + referringAgent.avatar" alt="Agent" @error="handleAgentAvatarError" />
                      <span v-else class="material-icons-round white--text" style="font-size: 32px;">person</span>
                    </v-avatar>
                    <div>
                      <h3 class="text-h6 font-weight-bold mb-1">{{ referringAgent.name }}</h3>
                      <div class="text-body-2 mb-1">
                        <a :href="'mailto:' + referringAgent.email" class="text-decoration-none">
                          <span class="material-icons-round mr-1" style="font-size: 16px; vertical-align: middle;">email</span>
                          {{ referringAgent.email }}
                        </a>
                      </div>
                      <div v-if="referringAgent.phone" class="text-body-2">
                        <a :href="'tel:' + referringAgent.phone" class="text-decoration-none">
                          <span class="material-icons-round mr-1" style="font-size: 16px; vertical-align: middle;">phone</span>
                          {{ referringAgent.phone }}
                        </a>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="agent-tier-info pa-4 rounded" style="background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%); border: 1px solid rgba(76, 175, 80, 0.3);">
                    <div class="text-center">
                      <v-chip color="success" dark class="mb-2">{{ referringAgent.tier_name }}</v-chip>
                      <div class="text-h4 font-weight-bold success--text">${{ formatCurrency(agentCommissionAmount) }}</div>
                      <div class="text-caption">Agent earns {{ referringAgent.tier_percentage }}% of ${{ formatCurrency(commissionSummary.commission_per_sale) }} per sale</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Commission Summary Cards -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="3">
              <v-card class="summary-card" elevation="2">
                <v-card-text class="text-center pa-4">
                  <span class="material-icons-round mb-2" style="font-size: 36px; color: #4caf50;">payments</span>
                  <div class="text-h5 font-weight-bold">${{ formatCurrency(currentPlan.amount) }}</div>
                  <div class="text-caption grey--text">Per Sale Commission</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="summary-card" elevation="2">
                <v-card-text class="text-center pa-4">
                  <span class="material-icons-round mb-2" style="font-size: 36px; color: #2196f3;">sell</span>
                  <div class="text-h5 font-weight-bold">{{ commissionSummary.total_sales }}</div>
                  <div class="text-caption grey--text">Total Sales</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="summary-card" elevation="2">
                <v-card-text class="text-center pa-4">
                  <span class="material-icons-round mb-2" style="font-size: 36px; color: #ff9800;">pending</span>
                  <div class="text-h5 font-weight-bold">${{ formatCurrency(commissionSummary.total_pending) }}</div>
                  <div class="text-caption grey--text">Pending Payout</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="summary-card" elevation="2">
                <v-card-text class="text-center pa-4">
                  <span class="material-icons-round mb-2" style="font-size: 36px; color: #9c27b0;">account_balance</span>
                  <div class="text-h5 font-weight-bold">${{ formatCurrency(commissionSummary.total_paid) }}</div>
                  <div class="text-caption grey--text">Total Paid</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Current Plan Section -->
          <v-card class="mb-6" elevation="3">
            <v-card-title class="text-h5 font-weight-bold primary--text">
              <span class="material-icons-round mr-2">verified</span>
              Current Plan ({{ currentPlan.status === 'approved' ? 'Approved' : 'Pending' }})
            </v-card-title>
            <v-card-text class="pa-6">
              <div class="commission-details">
                <div class="detail-item mb-3">
                  <strong>Structure:</strong> Flat ${{ formatCurrency(currentPlan.amount) }} per sold unit
                </div>
                <div class="detail-item mb-3">
                  <strong>Effective:</strong> {{ formatDate(currentPlan.effective_date) }}
                </div>
                <div class="detail-item mb-3">
                  <strong>Next Review:</strong> {{ formatDate(currentPlan.next_review_date) }}
                </div>
              </div>
              <div class="text-right mt-4">
                <v-btn
                  color="primary"
                  outlined
                  @click="showProposeChangeDialog = true"
                  class="text-capitalize"
                >
                  <span class="material-icons-round mr-2">edit</span>
                  Propose Change
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>

        <!-- Edit Commission Offer Section -->
        <v-card class="mb-6" elevation="3" v-if="showEditForm">
          <v-card-title class="text-h5 font-weight-bold orange--text">
            <span class="material-icons-round mr-2">edit_note</span>
            Edit Commission Offer (Draft)
          </v-card-title>
          <v-card-text class="pa-6">
            <v-form ref="commissionForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="4">
                  <v-radio-group v-model="draftPlan.structure_type" label="Commission Structure">
                    <v-radio label="Flat Rate" value="flat"></v-radio>
                    <v-radio label="Percentage" value="percentage"></v-radio>
                    <v-radio label="Hybrid" value="hybrid"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" md="8">
                  <div v-if="draftPlan.structure_type === 'flat'">
                    <v-text-field
                      v-model="draftPlan.flat_amount"
                      label="Flat Amount per Unit"
                      prefix="$"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                  <div v-if="draftPlan.structure_type === 'percentage'">
                    <v-text-field
                      v-model="draftPlan.percentage"
                      label="Percentage of Selling Price"
                      suffix="%"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                  <div v-if="draftPlan.structure_type === 'hybrid'">
                    <v-text-field
                      v-model="draftPlan.base_amount"
                      label="Base Amount"
                      prefix="$"
                      type="number"
                      outlined
                      class="mb-3"
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                    <v-text-field
                      v-model="draftPlan.bonus_percentage"
                      label="Bonus Percentage"
                      suffix="%"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="draftPlan.notes"
                    label="Additional Notes (Optional)"
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
              </v-row>

              <div class="text-right mt-4">
                <v-btn 
                  color="green" 
                  dark
                  @click="submitForReview"
                  :disabled="!formValid"
                  class="text-capitalize mr-3"
                >
                  <span class="material-icons-round mr-2">send</span>
                  Submit for Admin Review
                </v-btn>
                <v-btn 
                  color="grey" 
                  outlined
                  @click="cancelEdit"
                  class="text-capitalize"
                >
                  Cancel
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- History Section -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold">
            <span class="material-icons-round mr-2">history</span>
            History
          </v-card-title>
          <v-card-text class="pa-6">
            <v-timeline dense>
              <v-timeline-item
                v-for="(item, index) in commissionHistory"
                :key="index"
                :color="item.status === 'Approved' ? 'success' : 'info'"
                small
              >
                <div class="history-item">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div>{{ item.description }} ({{ item.status }})</div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Propose Change Dialog -->
    <v-dialog v-model="showProposeChangeDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Propose Commission Change</v-card-title>
        <v-card-text>
          <p>Would you like to propose a change to your current commission structure?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showProposeChangeDialog = false">Cancel</v-btn>
          <v-btn color="blue" @click="startEdit">Yes, Propose Change</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerCommission',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      isDarkMode: true,
      loading: false,
      userName: '',
      businessName: '',
      showEditForm: false,
      showProposeChangeDialog: false,
      formValid: false,
      wasabiUrl: process.env.VUE_APP_WASBI_URL || '',
      currentPlan: {
        structure_type: 'flat',
        amount: 0,
        effective_date: '',
        next_review_date: '',
        status: 'approved'
      },
      draftPlan: {
        structure_type: 'flat',
        flat_amount: 500,
        percentage: 3.0,
        base_amount: 200,
        bonus_percentage: 2.0,
        notes: ''
      },
      commissionHistory: [],
      referringAgent: null,
      agentCommissionAmount: 0,
      commissionSummary: {
        total_paid: 0,
        total_pending: 0,
        total_sales: 0,
        commission_per_sale: 0
      },
      rules: {
        required: value => !!value || 'This field is required',
        positive: value => value > 0 || 'Value must be greater than 0'
      }
    }
  },
  beforeCreate() {
    // Check authentication
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadDarkModePreference()
    await this.loadUserData()
    await this.loadCommissionData()
  },
  methods: {
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
    },
    async loadCommissionData() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')

        const response = await axios.get('/api/dealer/commission-data', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          const data = response.data.data
          this.currentPlan = data.current_plan || this.currentPlan
          this.commissionHistory = data.history || []
          this.referringAgent = data.referring_agent || null
          this.agentCommissionAmount = data.agent_commission_amount || 0
          if (data.summary) {
            this.commissionSummary = data.summary
          }
        }
      } catch (error) {
        console.error('Error loading commission data:', error)
        this.$toast.error('Error loading commission data')
      } finally {
        this.loading = false
      }
    },
    handleAgentAvatarError(event) {
      event.target.style.display = 'none'
    },
    formatCurrency(value) {
      if (!value) return '0.00'
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    handleNavigation() {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    startEdit() {
      this.showProposeChangeDialog = false
      this.showEditForm = true
    },
    cancelEdit() {
      this.showEditForm = false
      this.resetDraftPlan()
    },
    resetDraftPlan() {
      this.draftPlan = {
        structure_type: 'flat',
        flat_amount: 500,
        percentage: 3.0,
        base_amount: 200,
        bonus_percentage: 2.0,
        notes: ''
      }
    },
    async submitForReview() {
      if (!this.$refs.commissionForm.validate()) {
        return
      }

      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/propose-commission-change', this.draftPlan, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Commission change proposal submitted successfully!')
          this.showEditForm = false
          this.resetDraftPlan()
          await this.loadCommissionData()
        } else {
          this.$toast.error(response.data.message || 'Error submitting proposal')
        }
      } catch (error) {
        console.error('Error submitting commission proposal:', error)
        this.$toast.error('Error submitting commission proposal')
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}
.dealer-main.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
}

.page-header h1 {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.commission-details .detail-item {
  font-size: 16px;
  line-height: 1.6;
}

.history-item {
  padding: 8px 0;
}

.v-card {
  border-radius: 12px !important;
}

.v-card-title {
  padding: 20px 24px 16px 24px;
}

.v-card-text {
  padding: 0 24px 24px 24px;
}

/* Summary Cards */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

/* Agent Card */
.agent-card {
  border-left: 4px solid #4caf50;
}
.agent-tier-info {
  text-align: center;
}
</style>

<style>
/* Dark mode styles - unscoped for proper cascading */
.dark-mode .v-card {
  background: rgba(30, 30, 60, 0.8) !important;
  border: 1px solid rgba(138, 43, 226, 0.3);
}
/* Text colors */
.dark-mode .v-card-title,
.dark-mode .v-card-text,
.dark-mode .v-card-text *,
.dark-mode .v-list-item__title,
.dark-mode .v-list-item__subtitle,
.dark-mode .v-list-item__content,
.dark-mode .commission-details,
.dark-mode .detail-item,
.dark-mode .history-item,
.dark-mode .text-h4,
.dark-mode .text-h5,
.dark-mode .text-h6,
.dark-mode .text-subtitle-1,
.dark-mode .text-subtitle-2,
.dark-mode .text-body-1,
.dark-mode .text-body-2,
.dark-mode .text-caption,
.dark-mode p,
.dark-mode span:not(.v-btn__content span):not(.v-chip__content),
.dark-mode strong,
.dark-mode label,
.dark-mode .v-label,
.dark-mode .v-messages__message,
.dark-mode .v-input__slot,
.dark-mode .v-text-field input,
.dark-mode .v-textarea textarea,
.dark-mode .v-select__selection,
.dark-mode .v-radio .v-label,
.dark-mode .v-checkbox .v-label,
.dark-mode .v-switch .v-label,
.dark-mode .v-input--radio-group legend {
  color: #e0e0e0 !important;
}
/* Icons */
.dark-mode .v-icon,
.dark-mode .material-icons,
.dark-mode .material-icons-round,
.dark-mode .material-icons-outlined {
  color: #e0e0e0 !important;
}
/* Button icons should keep their color */
.dark-mode .v-btn .v-icon,
.dark-mode .v-btn .material-icons,
.dark-mode .v-btn .material-icons-round {
  color: inherit !important;
}
/* Colored text overrides */
.dark-mode .v-card-title.primary--text {
  color: #42a5f5 !important;
}
.dark-mode .v-card-title.orange--text {
  color: #ffa726 !important;
}
.dark-mode .v-card-title.green--text {
  color: #66bb6a !important;
}
.dark-mode .primary--text {
  color: #42a5f5 !important;
}
.dark-mode .success--text {
  color: #66bb6a !important;
}
.dark-mode .v-card-title.success--text {
  color: #66bb6a !important;
}
.dark-mode .summary-card {
  background: rgba(30, 30, 60, 0.9) !important;
}
.dark-mode .agent-tier-info {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(56, 142, 60, 0.2) 100%) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
}
.dark-mode .agent-card a,
.dark-mode .agent-card a:visited,
.dark-mode .agent-card .text-body-2 a {
  color: #e0e0e0 !important;
}
.dark-mode .agent-card a:hover {
  color: #ffffff !important;
}
.dark-mode .agent-card h3,
.dark-mode .agent-card .text-h6 {
  color: #ffffff !important;
}
</style>
